/**
 * Lightweight SVG GPS-trail visualizer.
 *
 * Avoids pulling in a tile library — renders trail points as a polyline on a
 * normalised viewport, with start/end markers and play-back time labels.
 *
 * Suitable for inspection-evidence printouts; for full live mapping we can
 * upgrade to react-leaflet later.
 */
import { useMemo } from "react";

export default function VisitTrail({ trail, expectedAddress, height = 280 }) {
  const points = trail || [];
  const bbox = useMemo(() => {
    if (points.length === 0) return null;
    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    if (expectedAddress?.lat != null) { lats.push(expectedAddress.lat); lngs.push(expectedAddress.lng); }
    const pad = 0.0008;
    return {
      minLat: Math.min(...lats) - pad, maxLat: Math.max(...lats) + pad,
      minLng: Math.min(...lngs) - pad, maxLng: Math.max(...lngs) + pad,
    };
  }, [points, expectedAddress]);

  if (!bbox || points.length === 0) {
    return <p className="text-sm text-slate-500">No GPS trail recorded for this visit.</p>;
  }

  const W = 600;
  const H = height;
  const project = (lat, lng) => {
    const x = ((lng - bbox.minLng) / (bbox.maxLng - bbox.minLng)) * W;
    const y = H - ((lat - bbox.minLat) / (bbox.maxLat - bbox.minLat)) * H;
    return [x, y];
  };

  const polyline = points.map((p) => project(p.lat, p.lng).join(",")).join(" ");
  const [startX, startY] = project(points[0].lat, points[0].lng);
  const [endX, endY] = project(points[points.length - 1].lat, points[points.length - 1].lng);
  let expectedXY = null;
  if (expectedAddress?.lat != null) {
    expectedXY = project(expectedAddress.lat, expectedAddress.lng);
  }

  return (
    <div className="space-y-2" data-testid="gps-trail">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full bg-slate-50 border border-slate-200 rounded-md">
        {/* Grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={`gx${f}`} x1={W * f} y1={0} x2={W * f} y2={H} stroke="#E2E8F0" strokeDasharray="2,3" />
        ))}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={`gy${f}`} x1={0} y1={H * f} x2={W} y2={H * f} stroke="#E2E8F0" strokeDasharray="2,3" />
        ))}
        {/* Expected service-user address */}
        {expectedXY && (
          <g>
            <circle cx={expectedXY[0]} cy={expectedXY[1]} r={36} fill="#0D7377" fillOpacity={0.10} stroke="#0D7377" strokeDasharray="4,3" />
            <circle cx={expectedXY[0]} cy={expectedXY[1]} r={6} fill="#0D7377" />
            <text x={expectedXY[0] + 10} y={expectedXY[1] - 6} fontSize="10" fill="#0D7377" fontWeight="600">SU home (200m)</text>
          </g>
        )}
        {/* Trail line */}
        <polyline points={polyline} fill="none" stroke="#0D7377" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {/* Start marker */}
        <g>
          <circle cx={startX} cy={startY} r={8} fill="#16A34A" />
          <text x={startX + 12} y={startY + 4} fontSize="10" fill="#16A34A" fontWeight="600">Check-in</text>
        </g>
        {/* End marker */}
        <g>
          <circle cx={endX} cy={endY} r={8} fill="#DC2626" />
          <text x={endX + 12} y={endY + 4} fontSize="10" fill="#DC2626" fontWeight="600">Check-out</text>
        </g>
        {/* Intermediate dots */}
        {points.slice(1, -1).map((p, i) => {
          const [x, y] = project(p.lat, p.lng);
          return <circle key={i} cx={x} cy={y} r={3} fill="#0D7377" fillOpacity={0.6} />;
        })}
      </svg>
      <p className="text-xs text-slate-500 flex items-center justify-between">
        <span>{points.length} GPS point{points.length === 1 ? "" : "s"}</span>
        <span>{(points[0].at || "").replace("T", " ").slice(0, 19)} → {(points[points.length - 1].at || "").replace("T", " ").slice(0, 19)}</span>
      </p>
    </div>
  );
}
